'use client';

import { Badge } from '@kamio/ui/components/badge';
import { type ReactNode, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

export type AuthMethod = 'google' | 'azure' | 'email' | 'phone';

const STORAGE_KEY = 'kamio:last-used-auth-method';

export function setLastUsedAuthMethod(method: AuthMethod) {
  try {
    localStorage.setItem(STORAGE_KEY, method);
  } catch {
    // localStorage unavailable (e.g. blocked); ignore
  }
}

function useLastUsedAuthMethod(): AuthMethod | null {
  const [method, setMethod] = useState<AuthMethod | null>(null);

  useEffect(() => {
    try {
      setMethod(localStorage.getItem(STORAGE_KEY) as AuthMethod | null);
    } catch {
      // ignore
    }
  }, []);

  return method;
}

export function LastUsedBadge({ method, className }: { method: AuthMethod; className?: string }) {
  const lastUsed = useLastUsedAuthMethod();

  if (lastUsed !== method) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className={cn(
        (className = 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'),
        'absolute -top-4 -right-6',
        className,
      )}
    >
      Last used
    </Badge>
  );
}

/**
 * Records the auth method as "last used" when a form inside it is submitted.
 */
export function LastUsedRecorder({
  method,
  children,
}: {
  method: AuthMethod;
  children: ReactNode;
}) {
  return <div onSubmitCapture={() => setLastUsedAuthMethod(method)}>{children}</div>;
}
